import { useQueries, useQuery } from "@tanstack/react-query";
import {
  checkHealthOptions,
  checkReadinessOptions,
  type CheckHealthResponse,
} from "@harmonyso/tenants-sdk";
import { CELLS, PRIMARY_CELL, cellListRequestOptions } from "@/lib/cells/cells";
import { cellQueryPolicy } from "@/lib/cells/cell-session";
import { combineCellHealth } from "@/lib/cells/cell-health";
import type { UseQueryResult } from "@tanstack/react-query";

const toCellHealth = (results: UseQueryResult<CheckHealthResponse>[]) =>
  combineCellHealth(
    CELLS.map((cell, index) => ({
      cell,
      health: results.at(index)?.data,
      // A failed background refetch keeps the status it already has.
      error: results.at(index)?.data
        ? null
        : (results.at(index)?.error ?? null),
      isLoading: !!results.at(index)?.isLoading,
    })),
  );

/**
 * The tenants service's health in every configured cell, and the primary
 * cell's readiness. A cell that can't be reached is reported in `failures`,
 * and `allHealthy` is true only when every cell answered healthy.
 */
export function useAllCellHealth({ readiness = true } = {}) {
  const health = useQueries({
    queries: CELLS.map((cell) => ({
      ...checkHealthOptions(cellListRequestOptions(cell.id)),
      ...cellQueryPolicy(cell),
    })),
    combine: toCellHealth,
  });
  const primaryReadiness = useQuery({
    ...checkReadinessOptions(cellListRequestOptions(PRIMARY_CELL.id)),
    enabled: readiness,
  });
  return {
    cells: health.cells,
    primary: health.primary,
    failures: health.failures,
    isLoading: health.isLoading,
    allHealthy: health.allHealthy,
    primaryReadiness,
  };
}
